import { datadogLogs } from '@datadog/browser-logs'
import { useInView } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { memo, useEffect, useMemo, useRef } from 'react'
import { Button } from 'ui/src/components/Button/Button'
import { ChevronIcon } from 'ui/src/icons/Chevron'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { DeductibleSelectorV2 } from '@/features/priceCalculator/PurchaseFormV2/OfferPresenterV2/DeductibleSelectorV2/DeductibleSelectorV2'
import { useTracking } from '@/services/Tracking/useTracking'
import { useActiveFormSectionId } from '../../useActiveFormSectionId'
import { usePriceCalculatorForm } from '../../usePriceCalculatorForm'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { usePriceIntent } from '../../usePriceIntent'
import { SectionTitle, SectionSubtitle } from '../SectionHeading'
import { TiersSection } from '../TierSection'
import { useSelectedOffer } from '../useSelectedOffer'
import { useTiersAndDeductibles } from '../useTiersAndDeductibles'
import { sectionsWrapper } from './OfferPresenterV2.css'
import { OfferSummary } from './OfferSummary'
import { TravelAddonsSelector } from './TravelAddonSelector/TravelAddonsSelector'

export const OfferPresenterV2 = memo(() => {
  const { t } = useTranslation('purchase-form')
  const [selectedOffer, setSelectedOffer] = useSelectedOffer()
  const { priceIntent } = usePriceIntent()
  const { setStep } = usePriceCalculatorStep()
  const { setActiveFormSectionId } = useActiveFormSectionId()
  const form = usePriceCalculatorForm()

  if (!priceIntent) {
    throw new Error('priceIntent must be defined')
  }

  if (selectedOffer == null) {
    throw new Error('selectedOffer must be defined')
  }

  const { tiers, deductibles } = useTiersAndDeductibles({
    offers: priceIntent.offers,
    selectedOffer,
  })

  const handleOfferChange = (offerId: string) => {
    const offer = priceIntent.offers.find((offer) => offer.id === offerId)

    if (offer === undefined) {
      datadogLogs.logger.error(`Unknown offer selected: ${offerId}`)
      return
    }

    setSelectedOffer(offer)
  }

  const handleEditInformation = () => {
    setStep('fillForm')
    // Make sure to always go to the last section of the form when editing
    const lastFormSectionId = form.sections[form.sections.length - 1].id
    setActiveFormSectionId(lastFormSectionId)
    // Make sure the user is viewing the start of the form when editing
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  const offerRef = useRef(null)
  const tracking = useTracking()
  const isInView = useInView(offerRef, { once: true })
  useEffect(() => {
    if (isInView) {
      tracking.reportViewItem(selectedOffer, 'store')
    }
  }, [selectedOffer, tracking, isInView])

  const selectedTier = useMemo(() => {
    const tier = tiers.find((item) => item.variant.typeOfContract === selectedOffer.variant.typeOfContract)

    if (tier === undefined) {
      datadogLogs.logger.warn(`Unknown tier selected`, {
        selectedOffer: selectedOffer.variant.typeOfContract,
        tiers: tiers.map((item) => item.variant.typeOfContract),
      })
      return tiers[0]
    }

    return tier
  }, [tiers, selectedOffer])

  return (
    <div className={yStack({ gap: { _: 'lg', lg: 9 } })} ref={offerRef}>
      <Button
        className={sprinkles({ marginRight: 'auto' })}
        size="small"
        variant="secondary"
        Icon={<ChevronIcon size="0.75rem" direction="left" />}
        onClick={handleEditInformation}
      >
        {t('OFFER_PRESENTER_EDIT_INFO_BUTTON_LABEL')}
      </Button>

      <div className={sectionsWrapper}>
        <TiersSection tiers={tiers} selectedTier={selectedTier} onTierChange={handleOfferChange} />

        {deductibles.length > 1 && (
          <section>
            <SectionTitle>{t('OFFER_PRESENTER_DEDUCTIBLE_TITLE')}</SectionTitle>

            <SectionSubtitle className={sprinkles({ marginBottom: 'lg' })}>
              {t('OFFER_PRESENTER_DEDUCTIBLE_SUBTITLE')}
            </SectionSubtitle>

            <div className={yStack({ alignItems: 'stretch', gap: 'md' })}>
              <DeductibleSelectorV2
                offers={deductibles}
                selectedOffer={selectedOffer}
                onValueChange={handleOfferChange}
              />
            </div>
          </section>
        )}

        <TravelAddonsSelector addons={selectedOffer.addons} />

        <OfferSummary />
      </div>
    </div>
  )
})
OfferPresenterV2.displayName = 'OfferPresenterV2'
